import React, { useState } from 'react';

const initialForm = {
  patient_name: '',
  patient_phone: '',
  patient_email: '',
  appointment_type: 'consulta',
  scheduled_at: '',
  assigned_role: 'doctora',
  status: 'programada',
  notes: '',
};

export const ClinicAppointmentsPanel = ({ workspace, onCreateAppointment, onUpdateAppointment, saving, canEdit, appRequests = [], onResolveAppRequest, onRunReminders }) => {
  const [form, setForm] = useState(initialForm);
  const [confirmTimes, setConfirmTimes] = useState({});

  const pendingRequests = appRequests.filter((request) => request.status === 'pendiente');

  const submit = async (event) => {
    event.preventDefault();
    const success = await onCreateAppointment(form);
    if (success) setForm(initialForm);
  };

  return (
    <section className="grid gap-5 xl:grid-cols-[0.9fr_1.1fr]" data-testid="clinic-appointments-panel">
      <form onSubmit={submit} className="rounded-[28px] border border-white/60 bg-white/95 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]" data-testid="clinic-appointments-form">
        <p className="text-xs uppercase tracking-[0.24em] text-[#0f766e]">Agenda compartida</p>
        <h2 className="mt-2 text-2xl font-black text-slate-900">Nueva cita</h2>
        <div className="mt-5 space-y-4">
          <input data-testid="clinic-appointment-patient-input" className="w-full rounded-2xl border border-slate-200 px-4 py-3" placeholder="Paciente" value={form.patient_name} onChange={(event) => setForm((prev) => ({ ...prev, patient_name: event.target.value }))} required />
          <div className="grid gap-4 sm:grid-cols-2">
            <input data-testid="clinic-appointment-phone-input" className="rounded-2xl border border-slate-200 px-4 py-3" placeholder="Teléfono" value={form.patient_phone} onChange={(event) => setForm((prev) => ({ ...prev, patient_phone: event.target.value }))} />
            <input data-testid="clinic-appointment-email-input" type="email" className="rounded-2xl border border-slate-200 px-4 py-3" placeholder="Email" value={form.patient_email} onChange={(event) => setForm((prev) => ({ ...prev, patient_email: event.target.value }))} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <select data-testid="clinic-appointment-type-select" className="rounded-2xl border border-slate-200 px-4 py-3" value={form.appointment_type} onChange={(event) => setForm((prev) => ({ ...prev, appointment_type: event.target.value }))}>
              {workspace.appointment_types.map((type) => <option key={type} value={type}>{type}</option>)}
            </select>
            <div className="flex gap-2">
              <div className="flex flex-col flex-1">
                <label className="text-[10px] font-bold uppercase text-slate-500">Fecha (día/mes/año)</label>
                <input data-testid="clinic-appointment-date-input" type="date" lang="es" className="rounded-2xl border border-slate-200 px-4 py-3" value={(form.scheduled_at || '').slice(0, 10)} onChange={(event) => setForm((prev) => ({ ...prev, scheduled_at: `${event.target.value}T${(prev.scheduled_at || '').slice(11, 16) || '12:00'}` }))} required />
              </div>
              <div className="flex flex-col w-32">
                <label className="text-[10px] font-bold uppercase text-slate-500">Hora</label>
                <input data-testid="clinic-appointment-time-input" type="time" className="rounded-2xl border border-slate-200 px-4 py-3" value={(form.scheduled_at || '').slice(11, 16) || '12:00'} onChange={(event) => setForm((prev) => ({ ...prev, scheduled_at: `${(prev.scheduled_at || '').slice(0, 10) || new Date().toISOString().slice(0, 10)}T${event.target.value}` }))} required />
              </div>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <select data-testid="clinic-appointment-role-select" className="rounded-2xl border border-slate-200 px-4 py-3" value={form.assigned_role} onChange={(event) => setForm((prev) => ({ ...prev, assigned_role: event.target.value }))}>
              {workspace.assignable_roles.map((role) => <option key={role.id} value={role.id}>{role.label}</option>)}
            </select>
            <select data-testid="clinic-appointment-status-select" className="rounded-2xl border border-slate-200 px-4 py-3" value={form.status} onChange={(event) => setForm((prev) => ({ ...prev, status: event.target.value }))}>
              {workspace.appointment_statuses.map((status) => <option key={status} value={status}>{status}</option>)}
            </select>
          </div>
          <textarea data-testid="clinic-appointment-notes-input" className="min-h-[120px] w-full rounded-2xl border border-slate-200 px-4 py-3" placeholder="Notas internas" value={form.notes} onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))} />
          <button data-testid="clinic-appointment-submit-button" type="submit" className="w-full rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-700" disabled={saving === 'appointment-create' || !canEdit}>
            {saving === 'appointment-create' ? 'Guardando…' : 'Guardar cita'}
          </button>
        </div>
      </form>

      <div className="space-y-4" data-testid="clinic-appointments-list">
        {canEdit && onRunReminders && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={onRunReminders}
              className="rounded-full bg-[#fef3c7] px-4 py-2 text-sm font-semibold text-[#92400e]"
              data-testid="clinic-run-reminders-button"
              disabled={saving === 'run-reminders'}
            >
              {saving === 'run-reminders' ? 'Enviando…' : 'Enviar recordatorios de mañana'}
            </button>
          </div>
        )}
        {pendingRequests.length > 0 && (
          <article className="rounded-[28px] border border-teal-200 bg-[#ecfdf5] p-5" data-testid="clinic-app-requests-card">
            <p className="text-xs uppercase tracking-[0.24em] text-[#0f766e]">Solicitudes desde la app del paciente</p>
            <div className="mt-3 space-y-3">
              {pendingRequests.map((request) => (
                <div key={request.id} className="rounded-[22px] bg-white p-4" data-testid={`clinic-app-request-${request.id}`}>
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <p className="text-sm font-bold text-slate-900">{request.patient_name}</p>
                      <p className="text-xs text-slate-500">{request.patient_phone || 'sin teléfono'} · Prefiere: {request.preferred_date} ({request.preferred_time})</p>
                      <p className="mt-1 text-xs text-slate-600">{request.reason}</p>
                    </div>
                    {canEdit && (
                      <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                        <input
                          type="datetime-local"
                          className="rounded-xl border border-slate-200 px-3 py-2 text-xs"
                          value={confirmTimes[request.id] || `${request.preferred_date}T${request.preferred_time === 'tarde' ? '16:00' : '10:00'}`}
                          onChange={(event) => setConfirmTimes((prev) => ({ ...prev, [request.id]: event.target.value }))}
                          data-testid={`clinic-app-request-datetime-${request.id}`}
                        />
                        <button
                          type="button"
                          onClick={() => onResolveAppRequest(request.id, { action: 'confirm', scheduled_at: confirmTimes[request.id] || `${request.preferred_date}T${request.preferred_time === 'tarde' ? '16:00' : '10:00'}` })}
                          className="rounded-full bg-[#0f766e] px-4 py-2 text-xs font-semibold text-white"
                          data-testid={`clinic-app-request-confirm-${request.id}`}
                          disabled={saving === `app-request-${request.id}`}
                        >
                          Confirmar cita
                        </button>
                        <button
                          type="button"
                          onClick={() => onResolveAppRequest(request.id, { action: 'reject' })}
                          className="rounded-full bg-rose-100 px-4 py-2 text-xs font-semibold text-rose-700"
                          data-testid={`clinic-app-request-reject-${request.id}`}
                          disabled={saving === `app-request-${request.id}`}
                        >
                          Rechazar
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </article>
        )}
        {workspace.appointments?.length ? (
          <p className="rounded-[28px] border border-white/60 bg-white/95 p-4 text-sm font-semibold text-slate-800" data-testid="clinic-appointments-summary">
            📅 {workspace.appointments.length} citas gestionadas desde el calendario de arriba: toca un día para ver, crear, editar o cancelar citas.
          </p>
        ) : <div className="rounded-[28px] border border-dashed border-slate-300 bg-white/80 p-8 text-sm text-slate-500" data-testid="clinic-appointments-empty">La agenda compartida está vacía.</div>}
      </div>
    </section>
  );
};
